import React, { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import SimpleInput from 'components/SimpleInput';
import { useAnalyticsEvent, useCustomToast } from 'hooks';
import { GA_LOGIN } from 'utils/constants';
import { isValidCPF } from 'utils/validations';
import * as z from 'zod';

interface SolicitacaoAcessoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormValues {
  empresa: string;
  cnpj: string;
  nome: string;
  cpf: string;
  dataNascimento: string;
  email: string;
  telefoneCelular: string;
}

const schema = z.object({
  empresa: z.string().nonempty('Empresa não pode ser vazia'),
  cnpj: z.string().min(14, 'CNPJ inválido').nonempty('CNPJ não pode ser vazio'),
  nome: z.string().nonempty('Nome não pode ser vazio'),
  cpf: z
    .string()
    .min(14, { message: 'CPF inválido' })
    .nonempty('CPF não pode ser vazio')
    .refine(value => isValidCPF(value), 'CPF inválido'),
  dataNascimento: z
    .string()
    .min(10, 'Data inválida')
    .nonempty('Data não pode ser vazia'),
  email: z
    .string()
    .email('E-mail inválido')
    .nonempty('E-mail não pode ser vazio'),
  telefoneCelular: z.string().nonempty('Telefone celular não pode ser vazio'),
});

export const SolicitacaoAcessoModal = ({
  isOpen,
  onClose,
}: SolicitacaoAcessoModalProps) => {
  const { handleToast } = useCustomToast();

  const analyticsEvent = useAnalyticsEvent({
    category: 'User',
    action: GA_LOGIN,
  });

  const [isLoading, setIsLoading] = React.useState(false);

  const {
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      empresa: '',
      cnpj: '',
      nome: '',
      cpf: '',
      dataNascimento: '',
      email: '',
      telefoneCelular: '',
    },
  });

  const onSubmit = useCallback(
    async (data: FormValues) => {
      setIsLoading(true);

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_URL}/api/SolicitacaoAcesso`,
          {
            empresa: data.empresa.trim(),
            cnpj: data.cnpj,
            nome: data.nome.trim(),
            cpf: data.cpf,
            dataNascimento: data.dataNascimento,
            email: data.email.trim(),
            telefoneCelular: data.telefoneCelular,
          },
        );

        const protocolo = response.data?.protocolo;

        handleToast({
          step: 5,
          action: 'Solicitação enviada!',
          successText: protocolo
            ? `Protocolo: ${protocolo}. Em breve entraremos em contato.`
            : 'Sua solicitação foi registrada. Em breve entraremos em contato.',
        });

        analyticsEvent('Solicitou novo acesso');
        reset();
        onClose();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Não foi possível enviar a solicitação.';

        handleToast({
          step: 4,
          action: 'enviar a solicitação',
          helpText: `Algo deu errado. ${message}`,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [analyticsEvent, handleToast, onClose, reset],
  );

  const handleClose = useCallback(() => {
    if (isLoading) return;
    reset();
    onClose();
  }, [isLoading, onClose, reset]);

  return (
    <Modal isCentered isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent py="36px" px="0px" bg="white">
        <ModalHeader
          p="0px"
          fontSize="xl"
          fontWeight={500}
          color="black"
          alignSelf="center"
        >
          Solicitar acesso
        </ModalHeader>
        <ModalCloseButton bg="grey.200" borderRadius="36px" color="black" />
        <ModalBody py="0px" px="40px" textAlign="center">
          <VStack w="100%" mb="16px" spacing="16px">
            <Text mt="8px" mb="8px" color="grey.600">
              Preencha os dados abaixo para solicitar acesso ao portal
            </Text>
            <SimpleInput
              {...register('empresa')}
              id="empresa"
              error={errors?.empresa?.message?.toString()}
              label="Empresa"
              placeholder="Nome da empresa"
              bg="transparent"
            />
            <Controller
              name="cnpj"
              control={control}
              render={({ field }) => (
                <SimpleInput
                  id="cnpj"
                  error={errors?.cnpj?.message?.toString()}
                  label="CNPJ"
                  type="cnpj"
                  placeholder="Digite o CNPJ"
                  bg="transparent"
                  value={field.value}
                  handleFill={value => field.onChange(value ?? '')}
                />
              )}
            />
            <SimpleInput
              {...register('nome')}
              id="nome"
              error={errors?.nome?.message?.toString()}
              label="Nome completo"
              placeholder="Digite seu nome"
              bg="transparent"
            />
            <Controller
              name="cpf"
              control={control}
              render={({ field }) => (
                <SimpleInput
                  id="cpf-solicitacao"
                  error={errors?.cpf?.message?.toString()}
                  label="CPF"
                  type="cpf"
                  placeholder="Digite seu CPF"
                  bg="transparent"
                  value={field.value}
                  handleFill={value => field.onChange(value ?? '')}
                />
              )}
            />
            <SimpleInput
              {...register('dataNascimento')}
              id="dataNascimento"
              error={errors?.dataNascimento?.message?.toString()}
              label="Data de nascimento"
              type="date"
              placeholder="Digite sua data de nascimento"
              bg="transparent"
            />
            <SimpleInput
              {...register('email')}
              id="email-solicitacao"
              error={errors?.email?.message?.toString()}
              label="E-mail"
              placeholder="Digite seu e-mail"
              bg="transparent"
            />
            <Controller
              name="telefoneCelular"
              control={control}
              render={({ field }) => (
                <SimpleInput
                  id="telefoneCelular"
                  error={errors?.telefoneCelular?.message?.toString()}
                  label="Telefone celular"
                  type="phone"
                  placeholder="Digite o telefone celular"
                  bg="transparent"
                  value={field.value}
                  handleFill={value => field.onChange(value ?? '')}
                />
              )}
            />
          </VStack>
        </ModalBody>

        <ModalFooter px="40px" pb="0px">
          <Button
            isLoading={isLoading}
            width="100%"
            onClick={handleSubmit(onSubmit)}
          >
            Enviar solicitação
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
